import {
  RegionCount,
  LabelResource,
  SearchCounts,
  BoardBasisCount,
} from "@search/api/types";

import {
  DEFAULT_LOCATION,
  EPOCH_DATE,
  SORT_MOST_POPULAR,
  SORT_CLOSEST,
} from "./constants";
import { Filters } from "./types";

export const buildFilters = (queryString: string) => {
  const params = new URLSearchParams(queryString);

  const filters: Filters = {
    boardBasis: [],
    bookOnline: false,
    date: EPOCH_DATE,
    eurotunnel: false,
    extraYards: false,
    flights: false,
    fourBall: false,
    location: DEFAULT_LOCATION,
    nights: 0,
    onlineExclusive: false,
    page: 1,
    price: [],
    regionIds: [],
    reviews: false,
    rounds: 0,
    sort: SORT_MOST_POPULAR,
    topPicks: false,
    tours: false,
  };

  const boardBasis = params.get("board_basis");
  if (boardBasis) {
    filters.boardBasis = boardBasis.split(",");
  }

  const bookOnline = params.get("book_online");
  if (bookOnline) {
    filters.bookOnline = bookOnline === "true";
  }

  const eurotunnel = params.get("le-shuttle");
  if (eurotunnel) {
    filters.eurotunnel = eurotunnel === "true";
  }

  const flights = params.get("flights");
  if (flights) {
    filters.flights = flights === "true";
  }

  const onlineExclusive = params.get("online_exclusive");
  if (onlineExclusive) {
    filters.onlineExclusive = onlineExclusive === "true";
  }

  const topPicks = params.get("top_picks");
  if (topPicks) {
    filters.topPicks = topPicks === "true";
  }

  const extraYards = params.get("extra_yards");
  if (extraYards) {
    filters.extraYards = extraYards === "true";
  }

  const tours = params.get("tours");
  if (tours) {
    filters.tours = tours === "true";
  }

  const date = params.get("date");
  if (date) {
    filters.date = new Date(date);
  }

  const days = params.get("days");
  if (days) {
    filters.days = days;
  }

  const fourBall = params.get("four_ball");
  if (fourBall) {
    filters.fourBall = fourBall === "true";
  }

  const location = params.get("location");
  if (location) {
    const [name, distance] = location.split(",");
    filters.location = {
      name,
      distance: parseInt(distance),
    };
  }

  const nights = params.get("nights");
  if (nights) {
    filters.nights = parseInt(nights, 10);
  }

  const page = params.get("page");
  if (page) {
    filters.page = parseInt(page, 10);
  }

  const price = params.get("price");
  if (price) {
    filters.price = price.split("-").map((i) => parseInt(i, 10));
  }

  const regionIds = params.get("region_ids");
  if (regionIds) {
    filters.regionIds = regionIds.split(",");
  }

  const reviews = params.get("reviews");
  if (reviews) {
    filters.reviews = reviews === "true";
  }

  const rounds = params.get("rounds");
  if (rounds) {
    filters.rounds = parseInt(rounds, 10);
  }

  const sort = params.get("sort");
  if (sort) {
    filters.sort = sort;
  } else if (location) {
    filters.sort = SORT_CLOSEST;
  }

  return filters;
};

const extractRegions = (
  regionCount: RegionCount,
  regionLabels: LabelResource = {},
): LabelResource => {
  Object.entries(regionCount).forEach(([key, value]) => {
    regionLabels[key] = value.name;
    extractRegions(value.children, regionLabels);
  });

  return regionLabels;
};

const extractBoardBasis = (counts: BoardBasisCount[]): LabelResource => {
  return counts.reduce((acc: LabelResource, { initial, name }) => {
    acc[initial] = name;

    return acc;
  }, {});
};

export const extractNameResources = (counts: SearchCounts): LabelResource => {
  const regionLabels = counts.regions ? extractRegions(counts.regions) : {};
  const boardBasisLabels = counts.boardBasis
    ? extractBoardBasis(counts.boardBasis)
    : {};

  return { ...regionLabels, ...boardBasisLabels };
};
